import { Text } from '@magicschool/spellbook/components/Text';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export type NotificationBadgeProps = React.ComponentProps<'div'> & VariantProps<typeof notificationBadgeVariants>;

export const notificationBadgeVariants = cva(
  [
    'inline-flex',
    'items-center',
    'justify-center',
    'w-fit',
    'shrink-0',
    'transition-background',
    'transition-colors',
    'outline-none',
    'rounded-full',
    'px-1',
    'max-w-18',
    'forced-colors:forced-color-adjust-none',
    'forced-colors:bg-[CanvasText]',
    'forced-colors:text-[Canvas]',
  ],
  {
    variants: {
      /** The visual appearance of the tag. */
      /**
       * @TODO [SPE-48] Rethink NotificationBadge variant/semantic names
       * https://linear.app/magicschool/issue/SPE-48/rethink-notificationbadge-variantsemantic-names
       */
      variant: {
        default: 'bg-error-solid-background text-error-solid-foreground-text', // Default variant is error semantic to stand out
        primary: 'bg-primary-solid-background text-primary-solid-foreground-text',
        defaultMuted: 'bg-error-muted-background text-error-text-strong',
        primaryMuted: 'bg-primary-muted-background text-primary-text-strong',
      },
      /** The size of the tag. */
      size: {
        xxs: 'h-2 max-h-2 min-w-2',
        xs: 'h-4 max-h-4 min-w-4',
        sm: 'h-5 max-h-5 min-w-5',
        md: 'h-6 max-h-6 min-w-6',
      },
      /** Ripples a ring outward from the badge: `arrival` three times on mount and on each count change, `persistent` until removed. Decorative, and skipped under `prefers-reduced-motion`. */
      pulse: {
        arrival: ['relative'],
        persistent: ['relative'],
      },
    },
  },
);

const notificationBadgePulseRingVariants = cva(
  ['pointer-events-none', 'absolute', 'inset-0', 'rounded-[inherit]', 'bg-inherit', 'opacity-0', 'motion-reduce:hidden'],
  {
    variants: {
      /** The ring's animation, matching the badge's `pulse`. */
      pulse: {
        arrival: ['animate-notification-pulse'],
        persistent: ['animate-notification-pulse-persistent'],
      },
    },
  },
);

/**
 * Non-interactive pill/dot that displays a numeric count or status indicator. Used for unread counts, selection counts, and attention dots.
 *
 * ### When to use
 * - Showing unread or pending counts on icons, buttons, or navigation items (e.g., unread messages, pending tasks)
 * - Displaying a selection count inside compound components like `FilterChipSelect`
 * - Drawing attention to an element with a small dot indicator (`size="xxs"`)
 *
 * ### When NOT to use
 * - For categorization labels or status tags: use `Badge` instead (text-based, supports variants like `outline` and `secondary`)
 * - For clickable indicators: this component is non-interactive, so pin it to a button with `NotificationAnchor`, or use `IconButton`'s `NotificationBadge` slot for a plain dot
 * - For inline status text without a count: use `Badge` or `Text` instead
 * - For a badge pinned to another element's corner: use `NotificationAnchor` instead
 *
 * ### API
 * - `variant`: Visual style: `'default'` (error/red, high attention), `'primary'` (brand color), `'defaultMuted'`, `'primaryMuted'`.
 * - `size`: `'xxs'` (dot only, content hidden visually), `'xs'`, `'sm'` (default), `'md'`.
 * - `children`: The count or text to display. At `size="xxs"`, children are visually hidden but remain accessible via `sr-only`.
 * - `pulse`: Optional ring that ripples outward: `'arrival'` three times on mount and again whenever the count changes, for something that just arrived, or `'persistent'` until removed, for something still waiting on the user. The count stays above the ring, and the ring is transparent once it settles.
 *
 * ### Visual states
 * - **Dot (`xxs`)**: Small colored circle with no visible text; content is screen-reader-only
 * - **Pill (`xs`/`sm`/`md`)**: Rounded pill that grows from circle to pill shape as content length increases
 * - **Overflow**: Has a built-in `max-w-18` to prevent excessive width; content truncates with ellipsis
 *
 * ### Accessibility
 * - Renders with `role="status"` and `aria-live="polite"` so count changes are announced to screen readers
 * - At `size="xxs"`, children are wrapped in a `sr-only` span so the content is still accessible
 * - Stays visible in forced-colors (Windows High Contrast) mode, where background colors are otherwise removed
 * - Use `aria-label` to provide descriptive context (e.g., `aria-label="3 unread messages"`) when the count alone is ambiguous
 * - `pulse` is decoration only and is skipped under `prefers-reduced-motion`; never rely on it to signal state
 */
export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  children,
  className,
  variant = 'default',
  size = 'sm',
  pulse,
  ...props
}) => {
  const textVariant = size === 'md' ? 'badge-1' : 'badge-2';
  return (
    <div
      className={cn(notificationBadgeVariants({ variant, size, pulse }), className)}
      data-spellbook-name="notification-badge"
      {...props}
      role="status"
      aria-live="polite"
    >
      {pulse ? (
        <span
          key={pulse === 'arrival' ? String(children) : undefined}
          aria-hidden="true"
          className={notificationBadgePulseRingVariants({ pulse })}
        />
      ) : null}
      {size === 'xxs' ? (
        <span className="sr-only">{children}</span>
      ) : (
        <Text variant={textVariant} element="span" color="inherit" className="relative truncate [text-box:trim-both_cap_alphabetic]">
          {children}
        </Text>
      )}
    </div>
  );
};
